// Reusable input objects
import * as inputs from '@/composables/builder/inputs';

// Block categories tabs
const categories = {
    content: 'Content Blocks',
    media: 'Media Blocks',
    hero: 'Hero Blocks',
    cta: 'CTA Blocks',
    dynamic: 'Dynamic Blocks',
};

export const getComponents = (api) => {
    // All options
    // https://github.com/builderio/builder/blob/ee8e6f2d/packages/core/src/builder.class.ts#L641
    // NOTE:
    // The category is used to group the blocks in the custom insert menu
    // This is a custom implementation and not part of the Builder.io API

    const agenda = {
        category: categories.content,
        component: resolveComponent('TwoFiveAgenda'),
        name: 'Agenda (List)',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F5f437924fe3442ee99e248072a20e5f6',
        inputs: [
            ...inputs.headlineCopy, 
            {
                name: 'items',
                friendlyName: 'Items',
                type: 'list',
                subFields: [
                    {
                        name: 'headline',
                        friendlyName: 'Headline',
                        type: 'string',
                    },
                    {
                        name: 'subHead',
                        friendlyName: 'Subhead',
                        type: 'string',
                    },
                    {
                        name: 'copy',
                        friendlyName: 'copy',
                        type: 'richText',
                    }
                ],
            },
        ]
    };

    const circleCarousel = {
        category: categories.content,
        component: resolveComponent('TwoSixStats'),
        name: 'circleCarousel',
        friendlyName: 'Circle Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F63b311de7fea46b4b8f3f5c75c6aaad6',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            {
                name: 'carouselCards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [{
                        name: 'largeText',
                        friendlyName: 'Large Text',
                        type: 'string',
                    },
                    {
                        name: 'smallText',
                        friendlyName: 'Small Text',
                        type: 'string',
                    },
                    {
                        name: 'shapeIndex',
                        friendlyName: 'Shard Shape',
                        type: 'number',
                        helperText: 'Which shard shape to use (0–3). Leave empty to auto-assign.',
                        enum: [
                            { label: 'Shape 1', value: 0 },
                            { label: 'Shape 2', value: 1 },
                            { label: 'Shape 3', value: 2 },
                            { label: 'Shape 4', value: 3 },
                        ],
                    },
                ],
                defaultValue: [{
                    largeText: '100',
                    smallText: 'Small Text',
                }, ],
            },
        ],
    };

    const quoteCarousel = {
        category: categories.content,
        component: resolveComponent('TwoSixQuoteCarousel'),
        name: 'Quote Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F5ff5d362b4fc45be96e684c3fabb6a47',
        inputs: [
            ...inputs.headline,
            {
                name: 'quoteCards',
                friendlyName: 'Quote Cards',
                type: 'list',
                subFields: [{
                        name: 'quote',
                        friendlyName: 'Quote',
                        type: 'string',
                    },
                    {
                        name: 'attribution',
                        friendlyName: 'Attribution',
                        type: 'string',
                    },
                    {
                        name: 'attributedTitle',
                        friendlyName: 'Attributed Title',
                        type: 'string',
                    },
                ],
                defaultValue: [{
                    quote: 'Quote',
                    attribution: 'Attribution',
                    attributedTitle: 'Attributed Title',
                }, ],
            },
        ],
    };

    const partnerDayGallery = {
        category: categories.content,
        component: resolveComponent('TwoSixPartnerDayGallery'),
        name: 'Partner Day Image Collage',
        inputs: [
            ...inputs.headlineSubhead,
        ],
    };

    const imageCollage = {
        category: categories.content,
        component: resolveComponent('TwoSixImageGallery'),
        name: 'Image Collage',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F4d6188db12d94056906a0104e2056d63',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'images',
                friendlyName: 'Images',
                type: 'list',
                subFields: [...inputs.image],
            },
            {
                name: 'quotes',
                friendlyName: 'Testimonials',
                type: 'list',
                subFields: [
                    {
                        name: 'quote',
                        friendlyName: 'Quote',
                        type: 'string',
                    },
                    {
                        name: 'name',
                        friendlyName: 'Name',
                        type: 'string',
                    },
                    {
                        name: 'title',
                        friendlyName: 'Title',
                        type: 'string',
                    }
                ],
            }
        ],
    };

    const sponsors = {
        category: categories.content,
        component: resolveComponent('TwoSixSponsors'),
        name: 'Sponsors',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9b53a657cea64dc5b53b27f4222e9cb2',
        inputs: [
            ...inputs.headline,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                defaultValue: 2026,
                helperText: 'Which year\'s sponsors to display.',
            },
        ],
    };

    const blogQuote = {
        category: categories.content,
        component: resolveComponent('TwoFiveBlogQuote'),
        name: 'Blog Quote',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fd76eceb8a7dd4d98910e06678b92033e',
        inputs: [{
            name: 'quote',
            friendlyName: 'Quote',
            type: 'string',
        }, ],
    };

    const festivalAccordion = {
        category: categories.content,
        component: resolveComponent('TwoSixFaq'),
        name: 'Festival Accordion',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fb8d8e55a18f44cf69d5c0a819a2fd4f6',
        inputs: [
            ...inputs.headlineSubhead,
            ...inputs.button,
            {
                name: 'teal',
                friendlyName: 'Use Teal Variant',
                type: 'boolean',
                defaultValue: false,
            },
            {
                name: 'accordionItems',
                friendlyName: 'Accordion Items',
                type: 'list',
                subFields: [...inputs.image, ...inputs.headlineCopy, ...inputs.anchor],
                defaultValue: [{
                    headline: 'Title',
                    content: 'Content',
                }, ],
            },
        ],
    };

    const stages = {
        category: categories.content,
        component: resolveComponent('TwoSixStages'),
        name: 'Stages',
        inputs: [
            {
                name: 'headline',
                friendlyName: 'Headline',
                type: 'string',
                defaultValue: 'UNBOUND Stages',
            },
            {
                name: 'copy',
                friendlyName: 'Intro Copy',
                type: 'richText',
            },
            {
                name: 'stages',
                friendlyName: 'Stages',
                type: 'list',
                subFields: [
                    {
                        name: 'name',
                        friendlyName: 'Stage Name',
                        type: 'string',
                        defaultValue: 'Main Stage',
                    },
                    {
                        name: 'badge',
                        friendlyName: 'Badge (optional)',
                        type: 'string',
                        helperText: 'Free text, e.g. "NEW!". Leave empty for no badge.',
                    },
                    ...inputs.image,
                    {
                        name: 'title',
                        friendlyName: 'Detail Title',
                        type: 'string',
                    },
                    {
                        name: 'description',
                        friendlyName: 'Detail Description',
                        type: 'richText',
                    },
                ],
                defaultValue: [{
                    name: 'Main Stage',
                    title: 'Main Stage',
                }],
            },
        ],
    };

    // Media Blocks

    const blogImageCarousel = {
        category: categories.media,
        component: resolveComponent('TwoFiveBlogImageCarousel'),
        name: 'Blog Image Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fee29365c16d340019b4ab2f7dc9da187',
        inputs: [{
            name: 'carouselCards',
            friendlyName: 'Carousel Cards',
            type: 'list',
            subFields: [
                ...inputs.image,
                {
                    name: 'caption',
                    friendlyName: 'Caption',
                    type: 'string',
                },
            ],
            defaultValue: [{
                image: {
                    src: 'https://picsum.photos/800/650',
                    alt: 'alt',
                },
                caption: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit vivamus, turpis curabitur metus.',
            }, ],
        }, ],
    };

    const blogVideo = {
        category: categories.media,
        component: resolveComponent('TwoFiveBlogVideo'),
        name: 'Blog Video Embed',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F28db6b9bb1fa4016a38614ec52fdb77f',
        inputs: [{
            name: 'videoEmbedCode',
            friendlyName: 'Video Embed Code',
            type: 'string',
            defaultValue: '<iframe loading="lazy" width="560" height="315" src="https://www.youtube.com/embed/J7dDoY1qXB8?si=4aY_YNOJY93Gfzvp" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
        }, ],
    };

    const braindates = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixBraindates'),
        name: 'Dynamic Braindate Block',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'buttonLabel',
                friendlyName: 'Button Label',
                type: 'string',
                defaultValue: 'Explore Braindates',
            },
            {
                name: 'buttonUrl',
                friendlyName: 'Button URL',
                type: 'string',
                defaultValue: 'https://unbound.braindate.com',
            },
            {
                name: 'randomize',
                friendlyName: 'Randomize',
                type: 'boolean',
                defaultValue: false,
            },
            {
                name: 'trending',
                friendlyName: 'Trending first (ignored when randomized)',
                type: 'boolean',
                defaultValue: false,
                helperText: 'Ranks by bookings, signups, bookmarks, reactions then views. Braindate reports all of these as zero until the event has activity, so until then this orders by start time like the default.',
            },
            {
                name: 'limit',
                friendlyName: 'Limit (ignored when curated)',
                type: 'number',
                defaultValue: 12,
            },
        ],
    };

    const videoEmbed = {
        category: categories.media,
        component: resolveComponent('TwoSixVideoEmbed'),
        name: 'Video Embed',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F28db6b9bb1fa4016a38614ec52fdb77f',
        inputs: [
            {
                name: 'videoEmbedCode',
                friendlyName: 'Video Embed Code',
                helperText: 'Paste the full iframe embed code from YouTube, Vimeo, etc.',
                type: 'longText',
                defaultValue: '<iframe width="1335" height="751" src="https://www.youtube.com/embed/1YwSHwGM8ws" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>',
            },
            {
                name: 'anchorId',
                friendlyName: 'Anchor ID',
                type: 'string',
            },
        ],
    };

    // Hero Blocks
    const hero = {
        category: categories.hero,
        component: resolveComponent('TwoSixHero'),
        name: 'Homepage Hero',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fffb44c5a272140ef8ad82b3a51a9b511',
        inputs: [
            ...inputs.headline,
            {
                name: 'subHead',
                friendlyName: 'Subhead Text',
                type: 'string',
                defaultValue: 'September 16 - 18 in Boston, MA',
            },
            {
                name: 'dates',
                type: 'object',
                subFields: [{
                        name: 'start',
                        type: 'date',
                    },
                    {
                        name: 'end',
                        type: 'date',
                    },
                ],
            },
            ...inputs.video,
            {
                name: 'youtubeID',
                friendlyName: 'Youtube ID',
                type: 'string',
                defaultValue: 'J7dDoY1qXB8',
            },
            {
                name: 'location',
                type: 'string',
            },
            {
                name: 'venue',
                type: 'string',
            },
            {
                name: 'copy',
                type: 'richText',
            },
            {
                name: 'bodyText',
                friendlyName: 'Smaller Copy',
                type: 'richText',
            },
            ...inputs.button,
        ],
    };

    const hero2026 = {
        category: categories.hero,
        component: resolveComponent('TwoSixHero'),
        name: 'Hero (2026)',
        inputs: [
            ...inputs.headline,
            {
                name: 'subHead',
                friendlyName: 'Subhead Text',
                type: 'string',
                defaultValue: 'September 16 - 18 in Boston, MA',
            },
            {
                name: 'bodyText',
                friendlyName: 'Smaller Copy',
                type: 'richText',
            },
            {
                name: 'copy',
                friendlyName: 'Smaller Body Text',
                type: 'richText',
            },
            ...inputs.video,
            ...inputs.button,
        ],
    };

    const manualEntryThreeUp = {
        category: categories.cta,
        name: 'Manual Entry Three Up',
        component: resolveComponent('TwoSixManualEntryThreeUp'),
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'hideTopDecorations',
                friendlyName: 'Hide Top Background Graphics',
                type: 'boolean',
                defaultValue: false,
                helperText: 'This will hide the background graphics on the top of the component and reduce its padding.',
            },
            {
                name: 'hideBottomDecorations',
                friendlyName: 'Hide Bottom Background Graphics',
                type: 'boolean',
                defaultValue: false,
                helperText: 'This will hide the background graphics on the top of the component and reduce its padding.',
            },            
            {
                name: 'cards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [...inputs.image, ...inputs.headlineCopy, ...inputs.button],
            },
        ],
    };

    const imageTextCta = {
        category: categories.cta,
        name: 'Image Text CTA',
        component: resolveComponent('TwoSixCta'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F580e428a1f4e45e7a81bbd84be728b33',
        inputs: [
            {
                name: 'variant',
                friendlyName: 'Variant',
                type: 'string',
                enum: ['purple', 'orange'],
                defaultValue: 'purple',
                helperText: 'Purple gradient (default) or orange gradient background.',
            },
            {
                name: 'imageShape',
                friendlyName: 'Image Shape',
                type: 'string',
                enum: ['shard', 'rectangle'],
                defaultValue: 'shard',
                helperText: 'Shard (default) crops the image into the angled shape — right for photos. '
                    + 'Rectangle keeps the same area but shows the whole image uncropped, for artwork '
                    + 'and device mockups that cannot be cut into.',
            },
            ...inputs.headlineCopy,
            ...inputs.image,
            ...inputs.button,
        ],
    };

    // CTA Blocks

    const imageTextTwoUp = {
        category: categories.cta,
        name: 'Image Text Two Up',
        component: resolveComponent('TwoSixManualEntryTwoUp'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fd1dd7a68f89c4140aad0b6e58d72a4f1',
        inputs: [{
            name: 'cards',
            type: 'object',
            friendlyName: 'Cards',
            subFields: [{
                    ...inputs.twoUpCard,
                    name: 'first_card',
                    friendlyName: 'First Card',
                },
                {
                    ...inputs.twoUpCard,
                    name: 'second_card',
                    friendlyName: 'Second Card',
                },
            ],
            defaultValue: {
                first_card: {
                    headline: 'Headline',
                    copy: '<p>Copy</p>',
                    image: {
                        src: 'https://picsum.photos/500/800',
                        alt: 'Alt',
                    },
                    button: {
                        label: 'Button Label',
                        title: 'Button Title',
                        url: 'https://inbound.com',
                    },
                },
                second_card: {
                    headline: 'Headline',
                    copy: '<p>Copy</p>',
                    image: {
                        src: 'https://picsum.photos/500/800',
                        alt: 'Alt Text',
                    },
                    button: {
                        label: 'Button Label',
                        title: 'Button title',
                        url: 'https://inbound.com',
                    },
                },
            },
        }, ],
    };

    const iconTitleContent = {
        category: categories.cta,
        name: 'Icon Title Content',
        component: resolveComponent('TwoSixIconTitleContent'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F31a9fd4a73dc4a15b2b642bd0b176534',
        inputs: [
            ...inputs.headline,
            ...inputs.image,
            {
                name: 'iconTitleContentCards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [...inputs.iconTitleContentCard],
                defaultValue: [{
                    headline: 'Headline',
                    copy: 'Copy',
                }, ],
            },
            {
                name: 'bottomText',
                friendlyName: 'Bottom Copy',
                type: 'richText',
                defaultValue: 'Lorem ipsum dolor sit amet consectetur adipiscing elit massa, penatibus scelerisque libero ridiculus leo platea fusce.',
            },
            ...inputs.button,
            {
                name: 'downloadLink',
                friendlyName: 'Download Link',
                type: 'object',
                subFields: [...inputs.button],
                defaultValue: {
                    href: 'https://inbound.com',
                    text: 'Download Link',
                },
            }
        ],
    };

    const imageContentCarousel = {
        category: categories.cta,
        component: resolveComponent('TwoSixNotableMoments'),
        name: 'Image Content Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F42d02426abc04bb6902b10528b6d05e2',
        inputs: [
            ...inputs.headline,
            {
                name: 'carouselCards',
                friendlyName: 'Carousel Cards',
                type: 'list',
                subFields: [
                    ...inputs.image,
                    {
                        name: 'mobileImage',
                        friendlyName: 'Mobile Image',
                        type: 'file',
                        allowedFileTypes: ['jpeg', 'jpg', 'png'],
                        helperText: 'Optional cropped image for mobile. Falls back to the desktop image if not set.',
                    },
                    ...inputs.headlineCopy,
                    ...inputs.button,
                    {
                        name: 'eyebrowText',
                        friendlyName: 'Eyebrow Text',
                        type: 'string',
                    },
                ],
            },
        ],
    };

    const imageTextCountdown = {
        category: categories.cta,
        component: resolveComponent('TwoSixImageTextCountdown'),
        name: 'Image Text Countdown',
        // screenshot:
        //   'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fa9ce8699751140bc825a4258488ef7c7',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            {
                ...inputs.button[0],
                name: 'secondButton',
                friendlyName: 'Second Button',
            },
            ...inputs.image,
            {
                name: 'subText',
                friendlyName: 'Subtext',
                type: 'string',
                defaultValue: 'Lorum',
            },
            {
                name: 'countdownDate',
                friendlyName: 'Countdown Date',
                type: 'date',
                defaultValue: 'September 17, 2024 03:24:00',
            },
            {
                name: 'straightBackground',
                friendlyName: 'Straight Background Version',
                type: 'boolean',
            },
        ],
    };

    const newsletterSignup = {
        category: categories.cta,
        component: resolveComponent('TwoSixNewsletterSignup'),
        name: 'Newsletter Signup.',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ffa3833db05224f8bbe73b6a668c357e6',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'subText',
                friendlyName: 'Subtext',
                type: 'richText',
            },
            {
                name: 'buttonText',
                friendlyName: 'Button Text',
                type: 'string',
            },
            {
                name: 'container',
                type: 'boolean',
                friendlyName: 'Enable Container',
            },
        ],
    };

    const videoExpanding = {
        category: categories.cta,
        component: resolveComponent('TwoSixSponsorVideo'),
        name: 'Expanding Video CTA',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F36481b30d987436998ccb06535f6fdb7',
        inputs: [{
                name: 'slides',
                friendlyName: 'Slides',
                type: 'list',
                subFields: [...inputs.headlineCopy, ...inputs.button],
            },
            ...inputs.video,
            {
                name: 'videoID',
                friendlyName: 'Video Id',
                type: 'string',
                defaultValue: '-ObCO3i_Tl0',
            },
        ],
    };

    const sessionSpeakerCarousel = {
        category: categories.dynamic,
        component: resolveComponent('TwoFiveSessionSpeakerCarousel'),
        name: 'Session Speaker Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fbea17d75a7de4409acca3ef9051dc603',
        inputs: [
            ...inputs.headline,
            {
                name: 'sessionId',
                friendlyName: 'Session ID',
                type: 'string',
                helperText: 'The session ID to pull speakers from. NOTE: If this block is used on a session page, the session ID will be automatically pulled from the page.',
            },
        ],
    };

    const registration = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixRegistration'),
        name: 'Registration',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ff45b80d2a8a244f8ac2663fcf08ac55d',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'date',
                friendlyName: 'Date Text',
                type: 'string',
                helperText: 'Displays to the right of the headline',
            },
            {
                name: 'location',
                friendlyName: 'Location Text',
                type: 'string',
                helperText: 'Displays to the right of the headline',
            },
            {
                name: 'showDetails',
                friendlyName: 'Show pass details',
                type: 'boolean',
            },
            {
                name: 'teal',
                friendlyName: 'Use hero background',
                helperText: 'Switch to the dark gradient hero variant with white text',
                type: 'boolean',
            },
            {
                name: 'bizzabo',
                friendlyName: 'Show Registration Widget',
                helperText: 'Display the Bizzabo registration widget below panels',
                type: 'boolean',
            },
            {
                name: 'useModal',
                friendlyName: 'Use Modal for Registration',
                helperText: 'Display the Bizzabo registration widget as a modal when clicking the register button',
                type: 'boolean',
            },
            {
                name: 'partnerBizzabo',
                friendlyName: 'Show Partner Day Widget',
                helperText: 'Display the Partner Day registration widget below panels',
                type: 'boolean',                
            },
            {
                name: 'showLowAvailability',
                friendlyName: 'Show "Low Availability" Flag (VIP)',
                helperText: 'Shows "low availability" beside the VIP register button once any VIP tier has sold out. Switch off to hide it.',
                type: 'boolean',
                defaultValue: true,
            },
            {
                name: 'showGaLowAvailability',
                friendlyName: 'Show "Low Availability" Flag (General Admission)',
                helperText: 'Shows "low availability" beside the General Admission register button once any GA tier has sold out. Off by default — switch on to show it.',
                type: 'boolean',
                defaultValue: false,
            },            
            {
                name: 'buttons',
                friendlyName: 'Buttons',
                helperText: 'Optional buttons displayed below the headline (hidden in hero mode)',
                type: 'list',
                subFields: [
                    {
                        name: 'label',
                        friendlyName: 'Label',
                        type: 'string',
                    },
                    {
                        name: 'url',
                        friendlyName: 'URL',
                        type: 'url',
                    },
                    {
                        name: 'newTab',
                        friendlyName: 'Open in new tab',
                        type: 'boolean',
                        defaultValue: false,
                    },
                ],
            },
            {
                name: 'links',
                friendlyName: 'Link Blocks',
                helperText: 'Displays beneath registration as large block',
                type: 'list',
                subFields: [
                    {
                        name: 'label',
                        friendlyName: 'Label',
                        type: 'string'
                    },
                    {
                        name: 'copy',
                        friendlyName: 'Description',
                        type: 'string'
                    },
                    {
                        name: 'url',
                        friendlyName: 'URL',
                        type: 'url'
                    },
                    {
                        name: 'buttonLabel',
                        friendlyName: 'Button Label',
                        type: 'string'
                    }
                ],
            }
        ],
    };

    const hubspotFormEmbed = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixHubspotFormEmbed'),
        name: 'Hubspot Form Embed',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F29de0ec787cd4b0ca764bbbaf5170db8',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'anchorId',
                friendlyName: 'Anchor Id',
                type: 'string',
            },
            {
                name: 'embedScript',
                friendlyName: 'Hubspot Form Embed Text',
                helperText: 'Paste the code supplied from hubspot here. This block will handle it from there!',
                type: 'string',
                defaultValue: '<script charset="utf-8" type="text/javascript" src="//js.hsforms.net/forms/embed/v2.js"></script> <script>   hbspt.forms.create({     region: "na1",     portalId: "146726",     formId: "efc5c433-5b6d-423a-aaab-f2fb40604f6c"   }); </script>',
            },
            {
                name: 'forceWhite',
                friendlyName: 'Use White Background',
                type: 'boolean',
            },
        ],
    };

    const blogHero = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixBlogHero'),
        name: 'Blog Hero',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fb51138cf449f44439cabca00009e8aaf',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'includeNewsletter',
                friendlyName: 'Include Newsletter',
                type: 'boolean',
            },
        ],
    };

    const blogThreeUp = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixBlogThreeUp'),
        name: 'Blog Three Up',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F78a68c13c9944defb9b4e60bbc821ae1',
        inputs: [
            ...inputs.headline,
            ...inputs.button,
            {
                name: 'backgroundColor',
                friendlyName: 'Background Color',
                type: 'color',
                defaultValue: '#F8F5EE',
            },
            {
                name: 'blog_posts',
                friendlyName: 'Blog Posts',
                type: 'list',
                subFields: [{
                    name: 'post',
                    friendlyName: 'Post',
                    type: 'reference',
                    model: 'blog',
                }, ],
            },
        ],
    };

    const blogFilterGrid = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixBlogFilterGrid'),
        name: 'Blog Filter Grid',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fe164f127ff9d422db251aada2173353e',
        inputs: [{
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'expandFiltersByDefault',
                friendlyName: 'Expand Filters By Default',
                type: 'boolean',
            },
            {
                name: 'categoryList',
                friendlyName: 'Categories',
                type: 'list',
                subFields: [{
                    name: 'name',
                    friendlyName: 'Display Name',
                    type: 'string',
                    required: true,
                }, {
                    name: 'slug',
                    friendlyName: 'Slug',
                    type: 'string',
                    helperText: 'URL-friendly identifier (e.g. "session-recaps"). Auto-generated from name if left blank.',
                }],
            },
        ],
    };

    const sessionsFeaturedCarousel = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixOnDemandSessions'),
        name: 'Sessions Featured Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F32feb4af077e4ae3bed8e3f410ad3dd1',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'treatAsHero',
                friendlyName: 'Treat As Hero',
                type: 'boolean',
            },
            ...inputs.button,
            {
                name: 'sessions',
                friendlyName: 'Sessions',
                type: 'list',
                subFields: [
                    {
                        name: 'title',
                        friendlyName: 'Session Title',
                        type: 'string',
                    },
                    {
                        name: 'url',
                        friendlyName: 'Session URL',
                        type: 'url',
                    },
                    {
                        name: 'tags',
                        friendlyName: 'Tags (comma separated)',
                        type: 'string',
                    },
                    {
                        name: 'speakers',
                        friendlyName: 'Speakers',
                        type: 'list',
                        subFields: [
                            {
                                name: 'name',
                                friendlyName: 'Speaker Name',
                                type: 'string',
                            },
                            {
                                name: 'avatar',
                                friendlyName: 'Speaker Avatar',
                                type: 'file',
                                allowedFileTypes: ['jpeg', 'jpg', 'png'],
                            },
                        ],
                    },
                    {
                        name: 'video',
                        friendlyName: 'Looping Video',
                        type: 'object',
                        subFields: [
                            {
                                name: 'mp4',
                                friendlyName: 'MP4 URL',
                                type: 'url',
                            },
                            {
                                name: 'webm',
                                friendlyName: 'WebM URL',
                                type: 'url',
                            },
                        ],
                    },
                    {
                        name: 'poster',
                        friendlyName: 'Still Frame / Poster',
                        type: 'file',
                        allowedFileTypes: ['jpeg', 'jpg', 'png'],
                    },
                ],
            },
        ],
    };

    const sessionsSchedule = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixSessionsSchedule'),
        name: 'Sessions - Full Schedule',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9c3306743a9b4ffaab65b4ce1fe42907',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                helperText: 'Filter sessions by event year (e.g. 2025). Leave empty for current year.',
            },
            {
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'enableFavorites',
                friendlyName: 'Enable Favorites',
                type: 'boolean',
            },
            {
                name: 'enableCalendar',
                friendlyName: 'Show Calendar Functionality',
                type: 'boolean',
            },
            {
                name: 'enableSeats',
                friendlyName: 'Enabled Saved Seats',
                type: 'boolean',
            },
            {
                name: 'enableAssistant',
                friendlyName: 'Enable Assistant',
                type: 'boolean',
            },            
        ],
    };

    const sessionsArchive = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixSessionsArchive'),
        name: 'Sessions - Archive',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9c3306743a9b4ffaab65b4ce1fe42907',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                helperText: 'Filter sessions by event year (e.g. 2025). Leave empty for current year.',
            },
            {
                name: 'unscheduled',
                friendlyName: 'Include Unscheduled',
                type: 'boolean',
                defaultValue: false,
                helperText: 'Include sessions that have not been assigned a date/time yet.',
            },
            {
                name: 'showAvailability',
                friendlyName: 'Show Availability Filter',
                type: 'boolean',
                defaultValue: false,
                helperText: 'Show the In-Person / On-Demand availability dropdown above the grid.',
            },
            {
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
        ],
    };

    const sessionsAgenda = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixAgenda'),
        name: 'Sessions - Agenda',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9c3306743a9b4ffaab65b4ce1fe42907',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                defaultValue: 2026,
                helperText: 'Conference year. Defaults to 2026 for the Full Agenda.',
            },
            {
                name: 'treatAsHero',
                friendlyName: 'Treat as Hero',
                type: 'boolean',
                defaultValue: true,
            },
            {
                name: 'unscheduled',
                friendlyName: 'Include Unscheduled',
                type: 'boolean',
                defaultValue: false,
            },
            {
                name: 'randomize',
                friendlyName: 'Randomize (preview only)',
                type: 'boolean',
                defaultValue: false,
                helperText: 'Dev preview — scrambles session times and seeds one of each card variant per day so every visual state has data. Leave off in production.',
            },
            {
                name: 'contactId',
                friendlyName: 'Contact ID',
                type: 'string',
                defaultValue: '',
            },
            {
                name: 'showBuilderBanner',
                friendlyName: 'Show Agenda Builder Banner',
                type: 'boolean',
                defaultValue: false,
                helperText: 'Shows a promo banner above the tabs that opens the Agenda Builder. Auto-hidden once the visitor already has a builder-generated schedule, and only while the builder is live.',
            },
        ],
    };

    const activities = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixActivities'),
        name: 'Activities',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'treatAsHero',
                friendlyName: 'Treat as Hero',
                type: 'boolean',
                defaultValue: true,
            },
            {
                name: 'anchorId',
                friendlyName: 'Anchor ID',
                type: 'string',
                defaultValue: 'activities',
                helperText: 'Used by the nav "My Favorites" deep-link (…?tab=favorites#<anchor>).',
            },
        ],
    };

    const sessionsCatalog = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixSessionsCatalog'),
        name: 'Sessions - Catalog',
        inputs: [
            ...inputs.headlineSubheadCopy,
            ...inputs.button,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                helperText: 'Filter sessions by event year (e.g. 2026). Leave empty for the current year.',
            },
            {
                name: 'sessions',
                friendlyName: 'Sessions',
                type: 'list',
                helperText: 'Optional — list session names to feature in order. Leave empty to auto-pull featured sessions for the year.',
                subFields: [
                    {
                        name: 'sessionTitle',
                        friendlyName: 'Session Title',
                        type: 'string',
                        helperText: 'Exact title of a session. Matched case-insensitively.',
                    },
                ],
            },
        ],
    };

    const speakersFeaturedCarousel = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixFeaturedSpeakers'),
        name: 'Speakers Featured Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fab9902189f024d368caeaf060910c7e7',
        inputs: [            
            {
                name: 'treatAsHero',
                friendlyName: 'Treat As Hero',
                type: 'boolean',
            },
            ...inputs.headlineSubheadCopy,
            ...inputs.button,
            {
                name: 'eyebrowText',
                friendlyName: 'Eyebrow Text',
                type: 'string',
            },
            {
                name: 'speakers',
                friendlyName: 'Speakers',
                type: 'list',
                subFields: [
                    {
                        name: 'name',
                        friendlyName: 'Speaker Name',
                        type: 'string'
                    },
                    {
                        name: 'title',
                        friendlyName: 'Speaker Title',
                        type: 'string'
                    },
                    {
                        name: 'bio',
                        friendlyName: 'Speaker Bio',
                        type: 'string'
                    },
                    ...inputs.image,
                    {
                        name: 'sponsorLogo',
                        friendlyName: 'Sponsor Logo',
                        type: 'object',
                        subFields: [
                            {
                                name: 'src',
                                friendlyName: 'Logo Image',
                                type: 'file',
                                allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
                            },
                            {
                                name: 'altText',
                                friendlyName: 'Alt Text',
                                type: 'string',
                            },
                        ],
                    },
                ],
            },
            {
                name: 'lazyLoad',
                friendlyName: 'Lazy Load',
                type: 'boolean',
                defaultValue: true,
            },
        ],
    };

    const speakersIndex = {
        category: categories.dynamic,
        component: resolveComponent('TwoSixSpeakersSpeakerGrid'),
        name: 'Speakers - Grid',
        inputs: [
            {
                name: 'treatAsHero',
                friendlyName: 'Treat As Hero',
                type: 'boolean',
                defaultValue: true,
            },
            ...inputs.headlineSubheadCopy,
            {
                name: 'year',
                friendlyName: 'Year',
                type: 'number',
                defaultValue: 2026,
                helperText: 'Event year to pull speakers from (e.g. 2026).',
            },
        ],
    };

    const blocks = [
        agenda,
        blogFilterGrid,
        blogHero,
        blogImageCarousel,
        blogQuote,
        blogThreeUp,
        blogVideo,
        braindates,
        videoEmbed,
        circleCarousel,
        festivalAccordion,
        hero,
        hubspotFormEmbed,
        iconTitleContent,
        imageCollage,
        partnerDayGallery,
        imageContentCarousel,
        imageTextCountdown,
        imageTextCta,
        imageTextTwoUp,
        manualEntryThreeUp,
        newsletterSignup,
        quoteCarousel,
        registration,
        sessionSpeakerCarousel,
        sessionsFeaturedCarousel,
        sessionsSchedule,
        sessionsAgenda,
        activities,
        sessionsArchive,
        sessionsCatalog,
        speakersFeaturedCarousel,
        speakersIndex,
        sponsors,
        stages,
        videoExpanding,
    ];

    return blocks;
};

//const setCategories = (api, blocks) => {
//    // if (process.client) return;
//
//    builder.init(api);
//
//    // https:github.com/BuilderIO/builder/blob/4f0dc79c1904a4133de6421056a02db8a493bed1/plugins/example-app-simple/src/plugin.ts#L5
//    Builder.register('editor.settings', {
//        customInsertMenu: true,
//        hideFormComponents: true,
//        hideDataTab: true,
//        // hideSymbols: true,
//        // hideTemplates: true,
//        hideAnimateTab: true,
//        hideVisualEditor: true,
//        hideImportTab: true,
//    });
//
//    Object.values(categories).forEach((category) => {
//        Builder.register('insertMenu', {
//            name: category,
//            items: blocks
//                .filter((block) => block.category === category)
//                .map((block) => {
//                    return {
//                        name: block.name,
//                    };
//                }),
//        });
//    });
//};
